/* eslint-disable camelcase */
import campaignsService from '@/api/campaignsService'
import channelService from '@/api/channelService'
import { CHANNEL_LABELS, CAMPAIGN_STATUSES, CHANNEL_PRODUCT_TYPES, CHANNEL_OPTIONS_EXTERIOR } from '@/utils/campaignOptions'
import layerService from '@/api/layerService'
import { mapMutations, mapActions, mapState } from 'vuex'
import { MODULE_NAME as MODULE_NAME_MAP, MUTATIONS as MUTATIONS_MAP } from '@/store/map/constants'
import { MODULE_NAME as MODULE_NAME_LOC, MUTATIONS as MUTATIONS_LOC, ACTIONS as ACTIONS_LOC } from '@/store/locations/constants'
import { multiPoint, bbox as getBbox } from '@turf/turf'
import authService, { USER_ROLES } from '@/api/authService'
import PointRadiusLayer from '@/api/PointRadiusLayer'
import locationsService from '@/api/locationsService'
import { clickOutside } from '@/directives/click-outside'
import { createAdvertIcon, ADVERT_COLOR_AVAILABLE, ADVERT_COLOR_RESERVED, ADVERT_COLOR_SELECTED } from '@/utils/mapUtils'
import AdvertsClusterLayer from '@/api/AdvertClusterLayer'
import getMostFrequent from '@/utils/getMostFrequent'
import i18n from '@/i18n'
import { ADVERT_TYPE_LABELS } from '@/utils/advertOptions'

const shopIconUrl = require('@/assets/icons/map/icon-store.svg')

const DEFAULT_KPIS = () => [
  { key: 'month_price', label: i18n.t('MONTH_PRICE'), value: 0 },
  { key: 'production_cost', label: i18n.t('PRODUCTION_COST'), value: 0 },
  { key: 'design_cost', label: i18n.t('DESIGN_COST'), value: 0 }
]

const defaultChannel = () => ({
  id: null,
  products: [],
  productsAdverts: [],
  direction: CHANNEL_PRODUCT_TYPES.REDIRECT.value,
  comment: undefined
})

function parseproductsAdverts (data) {
  const products = {}
  const productsAdverts = []

  for (const { product, advert, product_metadata } of data) {
    if (product_metadata && Object.keys(product_metadata).length > 0 && product_metadata.constructor === Object) {
      product.price = product_metadata.price
      product.sale_price = product_metadata.sale_price
      product.product_unit_type_1 = product_metadata.product_unit_type_1
      product.product_unit_type_2 = product_metadata.product_unit_type_2
    }
    products[product.id] = product
    productsAdverts.push({
      advert: advert.id,
      product: product.id
    })
  }

  return {
    products: Object.values(products),
    productsAdverts
  }
}

export default {
  name: 'ExteriorChannelConfig',
  mixins: [],
  components: {
    MapComponent: () => import(/* webpackChunkName: "MapComponent" */ '@/components/map-component/MapComponent.vue'),
    ChannelSidebar: () => import(/* webpackChunkName: "ChannelSidebar" */ '@/components/channel-sidebar/ChannelSidebar.vue'),
    ChannelHeader: () => import(/* webpackChunkName: "ChannelHeader" */ '@/components/channel-header/ChannelHeader.vue'),
    ExteriorChannelSidebar: () => import(/* webpackChunkName: "ExteriorChannelSidebar" */ '@/components/exterior-channel-sidebar/ExteriorChannelSidebar.vue'),
    ProductSelector: () => import(/* webpackChunkName: "ProductSelector" */ '@/components/product-selector/ProductSelector.vue'),
    Legend: () => import(/* webpackChunkName: "Legend" */ '@/components/legend/Legend.vue')
  },
  props: {},
  directives: {
    clickOutside
  },
  data () {
    return {
      currentProductId: null,
      shopLayerId: 'campaign_channel_shop',
      advertsLayerId: 'campaign_channel_adverts',
      loading: false,
      mapLoaded: false,
      campaign: undefined,
      boxSelectionEnabled: null,
      dragStart: null,
      dragCurrent: null,
      channel: defaultChannel(),
      kpisDirty: false,
      authData: authService.authData,
      advertPopup: {
        hidden: false,
        advert: null,
        product: [],
        style: {
          bottom: 0,
          left: 0
        },
        creativity: null
      },
      advertCreativity: null,
      advertCampaigns: {},
      kpis: {
        loading: false,
        data: DEFAULT_KPIS()
      }
    }
  },
  computed: {
    ...mapState(MODULE_NAME_LOC, ['adverts']),
    advertTypeLabels () {
      return ADVERT_TYPE_LABELS
    },
    campaignid () {
      return this.$route.params.campaignid
    },
    channelid () {
      return this.$route.params.channelid
    },
    isSignaling () {
      return this.$route.params.channeltype === CHANNEL_OPTIONS_EXTERIOR.SIGNALING
    },
    type () {
      return this.$route.params.channeltype
    },
    formValid () {
      const productsOk = this.channel.products.length
      const productsAdvertsOk = this.channel.products.every(p => (
        this.channel.productsAdverts.some(pa => pa.product === p.id)
      ))
      return !!(productsOk && productsAdvertsOk)
    },
    readonly () {
      return this.channel.id && this.campaign.status !== CAMPAIGN_STATUSES.DRAFT
      // const { CENTRAL_ACCEPTED, CENTRAL_REJECTED, AGENCY_CONFIRMED, FINISHED, ACTIVE } = CAMPAIGN_STATUSES
      // return this.channel.id && [CENTRAL_ACCEPTED, CENTRAL_REJECTED, AGENCY_CONFIRMED, FINISHED, ACTIVE].indexOf(this.campaign.status) !== -1
    },
    isServiceProduct () {
      return this.channel.direction === CHANNEL_PRODUCT_TYPES.DIRECT.value
    },
    hasBoxSelection () {
      return this.dragStart && this.dragCurrent
    },
    boxSelectionStyles () {
      if (!this.hasBoxSelection) {
        return { display: 'none' }
      }
      const minX = Math.min(this.dragStart.x, this.dragCurrent.x)
      const minY = Math.min(this.dragStart.y, this.dragCurrent.y)
      const maxX = Math.max(this.dragStart.x, this.dragCurrent.x)
      const maxY = Math.max(this.dragStart.y, this.dragCurrent.y)
      return {
        display: 'block',
        transform: `translate(${minX}px, ${minY}px)`,
        width: `${maxX - minX - 12}px`,
        height: `${maxY - minY - 12}px`
      }
    },
    isAgency () {
      return this.authData.user.role === USER_ROLES.AGENCY
    },
    isAdmin () {
      return this.authData.user.role === USER_ROLES.CENTRAL_ADMIN ||
        this.authData.user.role === USER_ROLES.ROOT
    },
    selectedAdvertIds () {
      return this.channel.productsAdverts.map(pa => pa.advert)
    },
    boxSelectionProduct () {
      return this.channel.products.find(p => p.id === this.boxSelectionEnabled)
    }
  },
  watch: {
    'channel.products': {
      handler (items, oldItems) {
        if (this.isSignaling && items.length) {
          this.advertPopup.product = items
        }
        if (this.mapLoaded && items.length !== oldItems.length) {
          this.kpisDirty = true
        }
      }
    },
    selectedAdvertIds (items, oldItems) {
      if (this.mapLoaded && items.length !== oldItems.length) {
        this.kpisDirty = true
        this.loadAdvertLayer()
      }
    }
  },
  async mounted () {
    this.unsubscribe = this.$store.subscribe(mutation => {
      if (mutation.type === `${MODULE_NAME_MAP}/${MUTATIONS_MAP.SET_VIEWSTATE}`) {
        this.closeAdvertPopup()
      }
    })
    try {
      const promises = [
        campaignsService.getCampaign({ id: this.campaignid }),
        this.channelid !== 'new' && channelService.getChannel({ id: this.channelid, type: this.type })
      ]
      const [campaign, channel] = await Promise.all(promises)
      if (channel) {
        this.channel = {
          ...this.channel,
          ...channel,
          ...parseproductsAdverts(channel.products_adverts)
        }
      }
      this.$nextTick(() => {
        this.campaign = campaign
      })
      await this.updateKPIs()
    } catch (err) {
      // eslint-disable-next-line
      console.error('[channel-config.js] Error loading data for channel config', err)
    }
  },
  beforeDestroy () {
    this[MUTATIONS_MAP.RESET_VIEWSTATE]()
    this[MUTATIONS_LOC.CLEAR_LOCATIONS]()
    layerService.removeLayer(this.shopLayerId)
    layerService.removeLayer(this.advertsLayerId)
    this.disableBoxSelection()
    if (this.unsubscribe) {
      this.unsubscribe()
    }
  },
  methods: {
    ...mapMutations(MODULE_NAME_MAP, [ MUTATIONS_MAP.SET_VIEWSTATE, MUTATIONS_MAP.RESET_VIEWSTATE, MUTATIONS_MAP.CENTER_BBOX ]),
    ...mapMutations(MODULE_NAME_LOC, [ MUTATIONS_LOC.SET_VIEWPORT_DATA, MUTATIONS_LOC.CLEAR_LOCATIONS ]),
    ...mapActions(MODULE_NAME_LOC, Object.values(ACTIONS_LOC)),
    async updateKPIs () {
      this.kpis.oading = true
      if (this.channel.id) {
        const data = await channelService.getExteriorKPIs(this.channel.id, this.type)
        for (const kpi of this.kpis.data) {
          kpi.value = data[kpi.key]
        }
      }
      this.kpis.loading = false
    },
    async onMapLoaded () {
      const shopids = this.campaign.shops.map(s => s.id)
      if (shopids.length > 0) {
        await this[ACTIONS_LOC.FETCH_ADVERTS]({
          shop_related__in: shopids,
          query: 0,
          // ignore_removed: true,
          page_size: 10000,
          reset: true,
          availability: true,
          advert_type: this.type,
          start_date: this.campaign.start_date,
          finish_date: this.campaign.finish_date,
          date: this.campaign.created_at
        })
        const advertCampaigns = await locationsService.getAdvertAvailabilityList({
          shop_related__in: shopids,
          page_size: 10000,
          availability: true,
          advert_type: this.type,
          start_date: this.campaign.start_date,
          finish_date: this.campaign.finish_date,
          date: this.campaign.created_at
        })
        this.advertCampaigns = Object.fromEntries(advertCampaigns.map(a => [a.id, a.campaigns]))
        await this.$nextTick()
        this.loadMap()
      }
      this.mapLoaded = true
    },
    loadMap () {
      this.loadAdvertLayer()
      this.createShopLayer()
      this.centerMap()
    },
    centerMap () {
      const data = this.campaign.shops.map(p => [p.lng, p.lat])
      const advertsCoords = this.adverts.data.map(a => [a.lng, a.lat])
      const geojson = multiPoint([...data, ...advertsCoords])
      const [minX, minY, maxX, maxY] = getBbox(geojson)
      this[MUTATIONS_MAP.CENTER_BBOX]({ bbox: [[minX, minY], [maxX, maxY]], maxZoom: 13 })
    },
    createShopLayer () {
      layerService.addLayer({
        id: this.shopLayerId,
        data: this.campaign.shops,
        layerType: PointRadiusLayer,
        pickable: true,
        getFillColor: [22, 150, 157, 51],
        getLineColor: [22, 150, 157],
        getRadius: 0,
        getPosition: d => [d.lng, d.lat],
        getIcon: () => ({
          width: 40,
          height: 40,
          url: shopIconUrl
        }),
        getTooltip ({ object }) {
          const text = `${object.name}`
          const style = {
            'background': 'var(--colorNegative)',
            color: 'var(--colorHigh)'
          }
          return { text, style, className: 'map-popup-white' }
        }
      })
    },
    loadAdvertLayer () {
      if (layerService.hasLayer(this.advertsLayerId)) {
        layerService.updateLayer(this.advertsLayerId, {
          updateTriggers: {
            getIcon: [this.selectedAdvertIds, this.boxSelectionEnabled]
          }
        })
      } else {
        layerService.addLayer({
          id: this.advertsLayerId,
          data: this.adverts.data,
          layerType: AdvertsClusterLayer,
          getPath: d => {
            return [
              [d.properties.shop_related.lng, d.properties.shop_related.lat],
              [...d.geometry.coordinates]
            ]
          },
          sizeScale: 32,
          textSizeScale: 20,
          pickable: true,
          getPosition: d => [d.lng, d.lat],
          getIcon: d => ({
            width: 40,
            height: 40,
            url: createAdvertIcon(this.getAdvertType(d), d.properties.cluster ? this.getClusterColor(d.properties) : this.getAdvertColor(d.properties))
          }),
          updateTriggers: {
            getIcon: [this.selectedAdvertIds, this.boxSelectionEnabled]
          },
          onClick: ({ x, y, object, objects }) => {
            if (this.boxSelectionProduct) {
              const adverts = object.properties.cluster
                ? objects.map(o => o.properties)
                : [object.properties]

              for (const advert of adverts) {
                const notReserved = this.advertCampaigns[advert.id].length === 0
                const sameShop = advert.shop_related.id === this.boxSelectionProduct.shop_related.id
                if (notReserved && sameShop) {
                  const selected = this.selectedAdvertIds.indexOf(advert.id) !== -1
                  if (selected) {
                    this.channel.productsAdverts = this.channel.productsAdverts.filter(pa => pa.advert !== advert.id)
                  } else {
                    this.channel.productsAdverts.push({
                      advert: advert.id,
                      product: this.boxSelectionProduct.id
                    })
                  }
                }
              }
            } else {
              if (!object.properties.cluster) {
                this.openAdvertPopup(x, y, object.properties)
              }
            }
          },
          getTooltip: ({ object }) => {
            const { advert_type: type, id, price } = object.properties
            const status = this.getAdvertStatusLabel(object.properties)
            const color = this.getAdvertColor(object.properties)
            const html = object.properties.cluster
              ? `<div>${object.properties.point_count} Soportes</div>`
              : `
                <div class="advert-popup" style="--color: ${color}">
                  <p class="advert-popup-title">
                    <span>${type} (ID ${id}) - </span>
                    <span class="advert-popup-status">${status}</span>
                  </p>
                  <p class="advert-popup-price">${price} €</p>
                </div>
              `
            const style = {
              'background': 'var(--colorNegative)',
              color: 'var(--colorHigh)'
            }
            return { html, style, className: 'map-popup-white' }
          }
        })
      }
    },
    openAdvertPopup (x, y, advert) {
      this.advertPopup.style.bottom = `calc(100vh - ${y}px)`
      this.advertPopup.style.left = `${x}px`
      this.advertPopup.advert = { ...advert, campaigns: this.advertCampaigns[advert.id] || [] }
      if (this.advertCampaigns[advert.id]) {
        let creativities = this.advertCampaigns[advert.id].map(c => {
          const ca = c.adverts.filter(a => a.id === advert.id)
          if (ca.length) {
            return ca[0]
          }
        })
        creativities.sort((a, b) => {
          return new Date(b.created_at) - new Date(a.created_at)
        })
        this.advertPopup.creativity = creativities[0].creativity
      }
      this.advertPopup.product = this.getProductForAdvert(advert.id)
    },
    closeAdvertPopup () {
      this.advertPopup.advert = null
      if (!this.isSignaling) {
        this.advertPopup.product = []
      }
      this.advertPopup.creativity = null
    },
    getAdvertColor (advert) {
      let color = null
      const selected = this.selectedAdvertIds.indexOf(advert.id) !== -1
      if (selected) {
        color = ADVERT_COLOR_SELECTED
      } else {
        const campaigns = this.advertCampaigns[advert.id] || []
        color = campaigns && campaigns.length ? ADVERT_COLOR_RESERVED : ADVERT_COLOR_AVAILABLE
      }

      const canBeSelected = this.boxSelectionProduct ? advert.shop_related.id === this.boxSelectionProduct.shop_related.id : true
      return `${color}${canBeSelected ? 'ff' : '88'}`
    },
    getAdvertType (feature) {
      return feature.properties.cluster
        ? getMostFrequent(feature.properties.advert_type.split('-'))
        : feature.properties.advert_type
    },
    getClusterColor ({ id, shop_related: shop }) {
      const colors = id.split('-').map(idstr => this.getAdvertColor({ id: Number(idstr), shop_related: shop }))
      const opacity = colors[0].substr(-2)
      if (colors.some(c => c.substr(0, 7) === ADVERT_COLOR_AVAILABLE)) {
        return `${ADVERT_COLOR_AVAILABLE}${opacity}`
      }
      if (colors.some(c => c.substr(0, 7) === ADVERT_COLOR_SELECTED)) {
        return `${ADVERT_COLOR_SELECTED}${opacity}`
      }
      if (colors.some(c => c.substr(0, 7) === ADVERT_COLOR_RESERVED)) {
        return `${ADVERT_COLOR_RESERVED}${opacity}`
      }
    },
    getAdvertStatusLabel (advert) {
      const selected = this.selectedAdvertIds.indexOf(advert.id) !== -1
      if (selected) {
        return i18n.t('ADVERT_STATUS_SELECTED')
      }

      const campaigns = this.advertCampaigns[advert.id] || []
      return campaigns && campaigns.length ? i18n.t('ADVERT_STATUS_RESERVED') : i18n.t('ADVERT_STATUS_AVAILABLE')
    },
    getProductForAdvert (advertId) {
      if (!this.isSignaling) {
        const productAdvert = this.channel.productsAdverts.find(pa => pa.advert === advertId)
        return productAdvert ? this.channel.products.filter(p => p.id === productAdvert.product) : []
      }
      return this.channel.products
    },
    clearProductForAdvertPopup () {
      if (!this.isSignaling) {
        this.advertPopup.product = []
      }
      this.updateProductForAdvertPopup()
    },
    updateProductForAdvertPopup () {
      const advertId = this.advertPopup.advert.id
      const product = this.advertPopup.product[0]

      this.channel.productsAdverts = this.channel.productsAdverts.filter(pa => pa.advert !== advertId)
      if (product) {
        this.channel.productsAdverts.push({ advert: advertId, product: product.id })
        if (!this.channel.products.find(p => p.id === product.id)) {
          this.channel.products.push(product)
        }
      }

      this.advertCampaigns[advertId] = []
      this.advertPopup.advert = null
    },
    toggleBoxSelection (productId) {
      if (productId) {
        this.enableBoxSelection(productId)
      } else {
        this.disableBoxSelection()
      }
    },
    enableBoxSelection (productId) {
      this.boxSelectionEnabled = productId
      layerService.deckInstance.setProps({
        controller: false,
        onDragStart: ({ x, y }) => {
          this.dragStart = { x, y }
        },
        onDrag: ({ x, y }) => {
          this.dragCurrent = { x, y }
        },
        onDragEnd: ({ x, y }) => {
          if (!this.hasBoxSelection) {
            return
          }

          const minX = Math.min(this.dragStart.x, x)
          const minY = Math.min(this.dragStart.y, y)
          const maxX = Math.max(this.dragStart.x, x)
          const maxY = Math.max(this.dragStart.y, y)

          const selection = layerService.deckInstance.pickObjects({
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
            layerIds: [this.advertsLayerId]
          })

          const product = this.boxSelectionProduct
          const newProductsAdverts = selection
            .map(({ object, objects }) => {
              if (!object) {
                return null
              }
              return object.properties.cluster
                ? objects.map(o => o.properties)
                : object.properties
            })
            .flat()
            .filter(a => {
              if (!a) {
                return false
              }

              const notSelected = this.selectedAdvertIds.indexOf(a.id) === -1
              const notReserved = this.advertCampaigns[a.id].length === 0
              const sameShop = a.shop_related.id === product.shop_related.id
              return notSelected && notReserved && sameShop
            })
            .map(a => ({ advert: a.id, product: productId }))

          this.channel.productsAdverts = this.channel.productsAdverts.concat(newProductsAdverts)

          this.dragStart = null
          this.dragCurrent = null
        }
      })
    },
    disableBoxSelection () {
      this.boxSelectionEnabled = null
      layerService.deckInstance.setProps({
        controller: true,
        onDragStart: null,
        onDrag: null,
        onDragEnd: null
      })
    },
    async saveChannel () {
      this.loading = true

      const productsAdverts = this.channel.products.map(p => {
        let basicData = {
          id: Number(p.id),
          adverts: this.channel.productsAdverts.filter(pa => pa.product === p.id).map(pa => pa.advert)
        }

        let metadata = {
          price: p.price,
          sale_price: p.sale_price,
          product_unit_type_1: p.product_unit_type_1,
          product_unit_type_2: p.product_unit_type_2
        }

        return { ...basicData, 'metadata': metadata }
      })

      const body = {
        id: this.channel.id,
        campaign: this.campaign.id,
        name: `${CHANNEL_LABELS[this.type]} - ${this.campaign.name}`,
        channel_type: this.type,
        start_date: this.campaign.start_date,
        finish_date: this.campaign.finish_date,
        campaign_start_date: this.campaign.start_date,
        campaign_finish_date: this.campaign.finish_date,
        direction: this.channel.direction,
        products_adverts: productsAdverts,
        comment: this.channel.comment
      }

      try {
        const channel = await channelService.saveChannel(body, this.campaign)
        if (!this.channel.id) this.channel.id = channel.id
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[channel-sidebar.js] Error saving channel', err)
        this.loading = false
        return
      }

      this.loading = false
      this.kpisDirty = false
      this.$router.replace({ path: `/exterior-campaigns/${this.campaignid}/${this.channel.id}/${this.type}` })
    }
  }
}
